import { Box, Chip, Grid, Typography, alpha, useTheme } from '@mui/material';
import { Warning as DeniedIcon, Schedule as PendingIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import { CategoryRequestCard } from '@/components/CategoryRequestCard';

interface CategoryRequest {
    id: number;
    categoryName: string;
    categoryIcon: string;
    requestType: 'join' | 'leave';
    message?: string;
    adminNote?: string;
    status: 'pending' | 'denied' | 'accepted';
}

interface CategoryRequestsSectionProps {
    pendingRequests: CategoryRequest[];
    deniedRequests: CategoryRequest[];
    onWithdraw: (id: number) => void;
    isDeleting: boolean;
    withdrawingId: number | null;
}

export const CategoryRequestsSection = ({
    pendingRequests,
    deniedRequests,
    onWithdraw,
    isDeleting,
    withdrawingId,
}: CategoryRequestsSectionProps) => {
    const { LL } = useI18nContext();
    const theme = useTheme();

    if (pendingRequests.length === 0 && deniedRequests.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                bgcolor: alpha(theme.palette.background.default, 0.4),
                borderRadius: 2,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
            >
                {LL.categoryRequests.yourRequests()}
            </Typography>

            {/* Pending Requests */}
            {pendingRequests.length > 0 && (
                <Box sx={{ mb: deniedRequests.length > 0 ? 2 : 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                        }}
                    >
                        <PendingIcon fontSize="small" color="warning" />
                        <Typography variant="caption" fontWeight={600}>
                            {LL.categoryRequests.pendingRequests()}
                        </Typography>
                        <Chip
                            label={pendingRequests.length}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                    </Box>
                    <Grid container spacing={1}>
                        {pendingRequests.map((req) => (
                            <CategoryRequestCard
                                key={req.id}
                                request={req}
                                onWithdraw={onWithdraw}
                                isWithdrawing={isDeleting && withdrawingId === req.id}
                            />
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Denied Requests */}
            {deniedRequests.length > 0 && (
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                        }}
                    >
                        <DeniedIcon fontSize="small" color="error" />
                        <Typography variant="caption" fontWeight={600}>
                            {LL.categoryRequests.deniedRequests()}
                        </Typography>
                        <Chip
                            label={deniedRequests.length}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                    </Box>
                    <Grid container spacing={1}>
                        {deniedRequests.map((req) => (
                            <CategoryRequestCard key={req.id} request={req} />
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};
